import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TickMark from '../../../../Assets/Modals/TickMark.svg';
import Minus from "../../../../Assets/MinusCheckbox.svg";
import CouponListItem from './CouponListItem';
import { CouponContext } from '../../../../Context/Coupons/CouponContext';
import { setListModal, setModal } from '../../../../Features/couponsSlice';
import { formatCouponType, stripTimeFromDate } from '../../../../lib/utils/utils';
import Modal from './Modals/Modal';

const getCouponStatus = (startDate, endDate, isVisible, isExpired) => {
    const today = stripTimeFromDate(new Date());
    const startDay = stripTimeFromDate(new Date(startDate));
    const endDay = stripTimeFromDate(new Date(endDate));

    const isInactive = !isVisible && !isExpired;
    const isTrulyExpired = (!isVisible && isExpired) || today > endDay || isExpired;

    if (isInactive) {
        return {
            name: 'Inactive',
            colors: 'text-[#666666] bg-[#EAEAEA]'
        };
    } else if (isTrulyExpired) {
        return {
            name: 'Expired',
            colors: 'text-[#991B1B] bg-[#FEE2E2]'
        };
    } else if (today < startDay) {
        return {
            name: 'Upcoming',
            colors: 'text-[#92400E] bg-[#FEF3C7]'
        };
    } else {
        return {
            name: 'Active',
            colors: 'text-[#065F46] bg-[#D1FAE5]'
        };
    }
};

function CouponList() {
    const dispatch = useDispatch();

    const coupons = useSelector((state) => state.coupons.coupons.coupons);

    const { selectedCoupons, setSelectedCoupons } = useContext(CouponContext);

    const [checkboxType, setCheckboxType] = useState('');

    const handleCheckboxClick = () => {
        if(selectedCoupons.length < coupons.length) {
            if(selectedCoupons.length === 0) {
                let listId = coupons.map(item => item.id);
                setSelectedCoupons(listId);
            }
            else {
                setSelectedCoupons([]);
            }
        }
        else {
            setSelectedCoupons([]);
        }
    }

    const handleDeleteList = () => {
        dispatch(setListModal('delete-confirm'));
    }

    const handleHideList = () => {
        dispatch(setListModal('hide-confirm'));
    }

    useEffect(() => {
        if(selectedCoupons.length < coupons?.length) {
            if(selectedCoupons?.length === 0) {
                setCheckboxType('');
            }
            else {
                setCheckboxType('half')
            }
        }
        else if(selectedCoupons?.length === 0) {
            setCheckboxType('');
        }
        else {
            setCheckboxType('full');
        }
    }, [selectedCoupons, coupons]);

    const handleCreateCoupon = () => {
        dispatch(setModal('select-coupon-type'));
    }

    return (
        <div className="flex flex-col mt-6 mx-5 select-none">
            <div className="flex bg-[#F5F5F5] font-semibold text-[#121212] text-[16px] leading-8">
                <div className="flex items-center gap-2 w-[calc((35/100)*(100%-44px))] px-[10px] py-2">
                    {
                        coupons.length > 0 &&
                        <div onClick={handleCheckboxClick} className={`cursor-pointer size-6 bg-[#FFFFFF] flex items-center justify-center rounded-md border ${checkboxType !== '' ? 'border-none bg-primaryColor' : 'border-[#D1D5DB]'} `} >
                            <img className='select-none' src={checkboxType === 'full' ? TickMark : checkboxType === '' ? undefined : Minus} alt="" />
                        </div>
                    }

                    <h2 className="">Coupon Name</h2>
                </div>

                <h2 className="w-[calc((18/100)*(100%-44px))] px-[10px] py-2 m-auto">Coupon Type</h2>
                <h2 className="w-[calc((13/100)*(100%-44px))] px-[10px] py-2 m-auto">Start Date</h2>
                <h2 className="w-[calc((13/100)*(100%-44px))] px-[10px] py-2 m-auto">End Date</h2>
                <h2 className="w-[calc((12/100)*(100%-44px))] px-[10px] py-2 m-auto">Status</h2>
                <h2 className="w-[calc((12/100)*(100%-44px))] px-[10px] py-2 m-auto">Mark as Active</h2>
                <div className="w-[44px]"></div>
            </div>

            {coupons?.map(coupon => {
                const isSelected = selectedCoupons.includes(coupon.id);
                let couponType = formatCouponType(coupon.typeCoupon);
                let status = getCouponStatus(coupon.startDate, coupon.endDate, coupon.isVisible, coupon.isExpired);

                return (
                    <CouponListItem 
                        key={coupon.id} 
                        coupon={coupon} 
                        isSelected={isSelected} 
                        couponType={couponType} 
                        status={status} 
                    />
                );
            })}

            {
                coupons.length === 0 &&
                <div className="mx-auto mt-20 flex flex-col items-center gap-6 w-3/5">
                    <div className="flex flex-col gap-2 text-[#666666] items-center">
                        <h1 className='font-semibold text-[18px] leading-7'>No coupons are added yet</h1>

                        <p className='text-center font-medium text-[16px] leading-7'>It looks like there aren't any coupons here just yet. Start adding coupons to manage and offer great deals to your customers. Ready to get started?</p>
                    </div>

                    <button onClick={handleCreateCoupon} className='outline-none flex items-center gap-[2px] px-4 py-2 bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText rounded'>Create Coupon</button>
                </div>
            }

            {
                selectedCoupons.length > 0 && 
                <div className="fixed left-1/2 -translate-x-1/2 bottom-12 border border-[#EAEAEA] bg-[#F5F5F5] rounded-xl p-4 flex items-center gap-3">
                    <p className='font-medium text-[14px] leading-5 text-[#121212]'>{selectedCoupons.length}/{coupons.length}</p>

                    <button onClick={handleDeleteList} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Delete</button>

                    <button onClick={handleHideList} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Hide</button>
                </div>
            }

            <Modal />
        </div>
    );
}

export default CouponList;
